import { Alert, Badge, Card, Skeleton, Stack, Tab, Tabs } from '@mui/material';
import { GridRowSelectionModel } from '@mui/x-data-grid';
import { Booking, Order } from '@thro-types/leihbar';

import { useQueryState } from 'nuqs';
import React, { useCallback, useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router';
import { Link } from 'react-router-dom';

import { useOrderQuery } from 'api/hooks/leihbar';
import { translations } from 'locales/i18n';
import { checkSinglePositive } from 'utils/checkSinglePositive';
import usePageHeading from 'utils/hooks/usePageHeading';

import { OrderDetailHeader } from '../components/OrderDetailHeader';
import { getOrderRowClassName } from '../helper';
import { PickingList } from './tabs/pickingList';
import { Pickup } from './tabs/pickup';
import { ReturnList } from './tabs/returnList';

export enum TabPanels {
  PICKING = 'picking',
  PICKUP = 'pickup',
  RETURN = 'return'
}

export const DailyOrderDetail = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();

  const [selectedBookings, setSelectedBookings] = useState<GridRowSelectionModel>([]);

  const [tab, setTab] = useQueryState('tab', { defaultValue: TabPanels.PICKING });

  const { data, isLoading } = useOrderQuery(parseInt(id!));

  usePageHeading(
    {
      heading: t(translations.pages.leihbar.daily.title()),
      headingProps: { back: location.state?.back ?? `/leihbar` }
    },
    [data, isLoading]
  );

  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, value: TabPanels) => {
      setSelectedBookings([]);
      setTab(value);
    },
    [setTab]
  );

  const getBookingsForPickingList = (bookings: Booking[]) => {
    return bookings.filter(booking => booking.state === 'BOOKED');
  };
  const getBookingsForPickUp = (bookings: Booking[]) => {
    return bookings.filter(booking => booking.state === 'PICKED' || booking.collectedAt);
  };
  const getBookingsForReturn = (bookings: Booking[]) => {
    return bookings.filter(booking => booking.state === 'COLLECTED' || booking.returnedAt);
  };

  const renderTabContent = (order: Order) => {
    switch (tab) {
      case TabPanels.PICKING:
        return (
          <PickingList
            bookings={getBookingsForPickingList(order.bookings || [])}
            selected={selectedBookings}
            handleSelect={setSelectedBookings}
          />
        );
      case TabPanels.PICKUP:
        return (
          <Pickup
            bookings={getBookingsForPickUp(order.bookings || [])}
            selected={selectedBookings}
            handleSelect={setSelectedBookings}
          />
        );
      case TabPanels.RETURN:
        return (
          <ReturnList
            bookings={getBookingsForReturn(order.bookings || [])}
            selected={selectedBookings}
            handleSelect={setSelectedBookings}
          />
        );
      default:
        break;
    }
  };

  const getPickingCount = (bookings: Booking[]) => {
    return bookings.filter(booking => booking.state === 'BOOKED' && !booking.deletedAt);
  };

  const getPickupCount = (bookings: Booking[]) => {
    return bookings.filter(booking => !booking.collectedAt && !booking.deletedAt);
  };

  const getReturnCount = (bookings: Booking[]) => {
    return bookings.filter(
      booking => booking.state === 'COLLECTED' && !booking.returnedAt && !booking.deletedAt
    );
  };

  useLayoutEffect(() => {
    const pickingCount = getPickingCount(data?.bookings || []);
    const pickupCount = getPickupCount(data?.bookings || []);
    const returnCount = getReturnCount(data?.bookings || []);

    if (checkSinglePositive(pickingCount.length, pickupCount.length, returnCount.length)) {
      if (pickingCount.length) {
        setTab(TabPanels.PICKING);
      } else if (pickupCount.length) {
        setTab(TabPanels.PICKUP);
      } else if (returnCount.length) {
        setTab(TabPanels.RETURN);
      }
    }
    // eslint-disable-next-line
  }, [data?.bookings]);

  if (!data) {
    return <Skeleton height={'60vh'} sx={{ transform: 'unset' }} />;
  }

  const filterBookingsForBatchCount = (bookings: Booking[], tab: TabPanels) => {
    switch (tab) {
      case TabPanels.PICKING:
        return getPickingCount(bookings);
      case TabPanels.PICKUP:
        return getPickupCount(bookings);
      case TabPanels.RETURN:
        return getReturnCount(bookings);
      default:
        return [];
    }
  };

  const getBookingCountForTab = (bookings: Booking[], tab: TabPanels) => {
    switch (tab) {
      case TabPanels.PICKING:
        bookings = getBookingsForPickingList(bookings);
        break;
      case TabPanels.PICKUP:
        bookings = getBookingsForPickUp(bookings);
        break;
      case TabPanels.RETURN:
        bookings = getBookingsForReturn(bookings);
        break;
      default:
        break;
    }
    return filterBookingsForBatchCount(bookings, tab).length;
  };

  const orderState = getOrderRowClassName(data);

  return (
    <Stack sx={{ height: '100%' }}>
      {orderState === 'overdue' && (
        <Alert severity="warning" sx={{ marginBottom: '0.5em' }}>
          {t(translations.pages.leihbar.daily.alerts.overdue())}
        </Alert>
      )}
      {orderState === 'conflicting' && (
        <Alert severity="error" sx={{ marginBottom: '0.5em' }}>
          {t(translations.pages.leihbar.daily.alerts.conflicting())}
          <br />
          {t(translations.pages.leihbar.daily.alerts.conflictingOrderList())}
          {data.affectedOrderIds?.map(orderId => (
            <Link to={`/leihbar/daily/${orderId}`} state={{ back: location }}>
              {orderId}
            </Link>
          ))}
        </Alert>
      )}
      <Card sx={{ padding: '0.8em', marginBottom: '0.5em' }}>
        <OrderDetailHeader order={data} setSelectionModel={setSelectedBookings} />
      </Card>
      <Tabs value={tab} onChange={handleTabChange} indicatorColor="primary">
        {Object.keys(TabPanels).map(tab => (
          <Tab
            value={tab.toLowerCase()}
            key={tab}
            label={
              <Badge
                color="primary"
                badgeContent={getBookingCountForTab(data.bookings || [], TabPanels[tab])}
              >
                {t(translations.pages.leihbar.orders.tabs[tab.toLowerCase()]())}
              </Badge>
            }
          />
        ))}
      </Tabs>
      {renderTabContent(data)}
    </Stack>
  );
};
